import React, {useEffect, useRef, useState} from "react";
import {useSearchParams, useNavigate} from "react-router-dom";
import {Button, Card, Col, Modal, ModalFooter, ModalHeader, Row, Spinner} from "reactstrap";
import {VsiBubble, VsiH4, VsiH6, VsiHr, VsiLgText, VsiSpanText} from "../../../styled/App.styled";
import ProgressBar from "../../../components/ProgressBar";
import AtAGlance from "../../../components/AtAGlance";
import { useVsiAppContext} from "../../../stores/VsiApp";
import Grid from "../../../components/Grid";
import {enterpriseGridDefs} from "../../../utils/gridColumnDefs/storage/allUnits";
import {getReport, PostAuth} from "../../../utils/Verbs";
import {useTitleFromComponent} from "../../../utils/hooks";
import {
    beforeAfterOld,
    entSummaryDeviceCountsColors,
    formatBarData,
} from "../../../utils/chartUtils";
import {formatAagValue, handleAagTitle} from "../../../utils/Helpers";
import ColumnChart from "../../../components/ColumnChart";
import {Link} from "react-router-dom";
import DonutChart from "../../../components/DonutChart";
import {roundToNearestHundredth} from "../../../utils/math";
import themeStyles, { vsiTitleTextColor } from "../../../utils/themeStyles";

const percentUsedProgressConfig = {
    type: "single",
    fields: ["percent_used"],
    thresholds: [
        [70, entSummaryDeviceCountsColors[3]],
        [80, entSummaryDeviceCountsColors[2]],
        [90, entSummaryDeviceCountsColors[1]],
        [100, entSummaryDeviceCountsColors[0]],
    ],
    multiply: true,
    denominator: 100,
    title: "% Used of Usable",
};

const percentIopsProgressConfig = {
    type: "single",
    fields: ["percent_iops"],
    multiply: true,
    thresholds: [
        [70, entSummaryDeviceCountsColors[3]],
        [80, entSummaryDeviceCountsColors[2]],
        [90, entSummaryDeviceCountsColors[1]],
        [100, entSummaryDeviceCountsColors[0]],
    ],
    title: "% IOPS Used",
    denominator: 100,
};

const entAlertsProgressConfig = {
    type: "multi",
    key: "enterprise_alerts",
    fields: ["green", "yellow", "red"],
    colors: ["#4CF84C", "#F7FF00", "#F84C4C"],
    title: "Enterprise Alerts",
    denominator: 100,
};

const enterpriseProgressConfig = [
    // percentUsedProgressConfig,
    // percentAllocatedProgressConfig,
    // percentIopsProgressConfig,
    entAlertsProgressConfig,
];

const utilAag = [
    {
        field: "total_used",
        mobile: true,
        title: "Used",
    },
    {
        field: "total_usable",
        mobile: true,
        title: "Usable"
    },
    {
        field: "total_free",
        diff: true,
        mobile: true,
        title: "Free",
    },
];

const countsAag = [
    {
        field: "total_iops",
        title: "IOPS",
        mobile: true,
    },
    {
        field: "total_latency",
        title: "Latency",
        diff: true,
        mobile: true,

    },
    {
        field: "mb_sec",
        title: "MB/s",
        diff: true,
        mobile: true,

    },
];

const miscAag = [
    // {
    //      field: "percent_allocated",
    //      mobile: true,
    //  },
    //  {
    //      field: "effective_free_space",
    //      title: "Effective Free",
    //      mobile: true,
    //  },
    //  {
    //      field: "total_snap",
    //      diff: true,
    //      mobile: true,
    //
    //  },
    {
        field: "dedupe",
        icon: true,
        diff: true,
        mobile: true,

    },
    {
        field: "total_hosts",
        diff: true,
        mobile: true,

    },
    {
        field: "orphans",
        diff: true,
        mobile: true,

    },
    {
        field: "total_used",
        mobile: true,
        title: "Used",
    },
    {
        field: "total_usable",
        mobile: true,
        title: "Usable",
    }, {
        field: "total_allocated",
        mobile: true,
        title: "Allocated",
    },
    {
        field: "raw_disk",
        title: "Raw",
        mobile: true,
    },
];

const deviceCountsByUtilCategories = ["> 90%", "80-89%", "70-79%", "< 70%"];
const deviceCountsByLatencyCategories = ["No Latency", ".01-1ms", "1-2ms", "> 2ms"];

export default function EnterpriseSummary({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const [searchParams] = useSearchParams();
    const [isLoading, setIsLoading] = useState(true);
    const [enterpriseGrid, setEnterpriseGrid] = useState([]);
    const [dashboard, setDashboard] = useState({});
    const [atAGlanceData, setAtAGlanceData] = useState({});
    const [deviceCountByUtilPieData, setDeviceCountByUtilPieData] = useState([]);
    const [deviceCountBarData, setDeviceCountBarData] = useState([]);
    const [redLightArrayHeight, setRedLightArrayHeight] = useState(0);
    const [rowSelected, setRowSelected] = useState(null);
    const [showWarningModal, setShowWarningModal] = useState(false);

    let navigate = useNavigate();

    const barChartDivRef = useRef(null); // used for ensuring titles are same height (this is so brilliant holy moly I love React)

    const { theme, client, periodId, user, getAccessTokenSilently } = useVsiAppContext();

    const titleTextColor = themeStyles[theme].titleTextColor;

    const getAtAGlance = function (data, percentAllocated, effective_total_free) {
        const reduceArray = {};
        const uniqueDevices = {};
        let subTotalIOPs = 0;
        let weightedIOPsLat = 0;
        data.forEach(deviceData => {
            const deviceKey = `${deviceData.device_name}_${deviceData.wwn}`;
            if (!uniqueDevices.hasOwnProperty(deviceKey) && !Boolean(deviceData.virtualized)) {
                uniqueDevices[deviceKey] = true;
                for (let [key, val] of Object.entries(deviceData)) {
                    if (reduceArray[key]) {
                        reduceArray[key] += val;
                    } else {
                        reduceArray[key] = val;
                    }
                }
                let calculatedLatency = (deviceData?.read_latency || 0 + deviceData?.write_latency || 0) / 2;
                subTotalIOPs += deviceData?.iops;
                weightedIOPsLat += deviceData?.iops * (deviceData?.avg_latency || calculatedLatency);
            }
        });

        setAtAGlanceData({
            ...atAGlanceData,
            raw_disk: reduceArray.raw_disk_gb,
            total_usable: reduceArray.usable_gb,
            total_used: reduceArray.total_used,
            total_free: reduceArray.total_free,
            effective_free_space: effective_total_free,
            percent_allocated: (percentAllocated * 100).toFixed(2),
            total_pools: reduceArray.num_disk_groups,
            total_hosts: reduceArray.num_hosts,
            total_snap: reduceArray.snap_gb,
            orphans: `${reduceArray.num_orphans?.toLocaleString?.("en-US", {maximumFractionDigits: 0}) ?? 0} (${reduceArray?.orphan_gb?.toLocaleString?.("en-US", {maximumFractionDigits: 0}) ?? 0} GiB)`,
            dedupe: reduceArray.dedupe_gb,
            total_allocated: reduceArray.total_allocated,
            total_iops: roundToNearestHundredth(reduceArray.iops),
            mb_sec: roundToNearestHundredth(reduceArray.mb_sec),
            total_latency: roundToNearestHundredth(weightedIOPsLat / subTotalIOPs),
        });

    };

    const getDashboard = function (data) {
        let totalUsable = 0;
        let totalUsed = 0;
        let totalAllocated = 0;
        let totalIops = 0;
        let totalIopsCapacity = 0;
        let ok = 0;
        let warnings = 0;
        let alerts = 0;
        let effective_total_free = 0;
        const uniqueDevices = {};
        // var maxIops = 0;
        // var notMaxIops = 0;

        for (let i = 0; i < data.length; i++) {
            const deviceKey = `${data[i].device_name}_${data[i].wwn}`;
                if (!uniqueDevices.hasOwnProperty(deviceKey)) {
                    if (!Boolean(data[i].virtualized)) {
                                     uniqueDevices[deviceKey] = true;
                    totalUsable = totalUsable + data[i].usable_gb;
                    totalUsed = totalUsed + data[i].total_used;
                    totalAllocated = totalAllocated + data[i].total_allocated;
                    totalIops = totalIops + data[i].iops;
                    if (parseInt(data[i].iops) !== 0) {
                        if (data[i].iops_capacity === null) {
                            totalIopsCapacity = totalIopsCapacity + data[i].max_iops_capacity;
                        } else {
                            totalIopsCapacity = totalIopsCapacity + data[i].iops_capacity;
                        }
                    }

                    if (data[i].array_status === "Array is active") {
                        effective_total_free = effective_total_free + (Math.floor(data[i].total_free / 4096) * 4096)
                    }
                }

                if (parseFloat(data[i].max_alert_level) === 0.5) {
                    warnings++;
                } else if (parseFloat(data[i].max_alert_level) === 0) {
                    alerts++;
                } else if (parseFloat(data[i].max_alert_level) === 1) {
                    ok++;
                }
            }
        }

        setDashboard({
            ...dashboard,
            percent_used: totalUsed / totalUsable,
            percent_allocated: (totalAllocated / totalUsable),
            green: ok,
            yellow: warnings,
            red: alerts,
            percent_iops: totalIopsCapacity && totalIopsCapacity > 0 ? (totalIops / totalIopsCapacity) : 0.00
        });

        const percentAllocated = totalUsable && totalUsable > 0 ? (totalAllocated / totalUsable) : 0.00;
        getAtAGlance(data, percentAllocated, effective_total_free);
    };

    const formatDeviceCountByUtilPieData = function (entData) {
        const tracker = {};
        const uniqueDevices = {};
        deviceCountsByUtilCategories.forEach(cat => {
            if (!tracker[cat]) {
                tracker[cat] = {};
            }
            tracker[cat].name = cat;
            tracker[cat].y = 0;
        });

        entData.forEach(datum => {
            const deviceKey = `${datum.device_name}_${datum.wwn}`;
            if (!uniqueDevices.hasOwnProperty(deviceKey) && !Boolean(datum.virtualized)) {
                uniqueDevices[deviceKey] = true;
                const pctUsed = datum.percent_used;
                if (pctUsed >= 90) {
                    tracker[deviceCountsByUtilCategories[0]].y = tracker[deviceCountsByUtilCategories[0]].y + 1;
                } else if (pctUsed >= 80 && pctUsed < 90) {
                    tracker[deviceCountsByUtilCategories[1]].y = tracker[deviceCountsByUtilCategories[1]].y + 1;
                } else if (pctUsed >= 70 && pctUsed < 80) {
                    tracker[deviceCountsByUtilCategories[2]].y = tracker[deviceCountsByUtilCategories[2]].y + 1;
                } else {
                    tracker[deviceCountsByUtilCategories[3]].y = tracker[deviceCountsByUtilCategories[3]].y + 1;
                }
            }
        });

        return deviceCountsByUtilCategories.map(cat => tracker?.[cat] ?? {});
    }

    const formatDeviceCountsBarData = function (entData) {
        const tracker = {};
        const uniqueDevices = {};
        deviceCountsByLatencyCategories.forEach(cat => {
            if (!tracker[cat]) {
                tracker[cat] = {};
            }
            tracker[cat].name = cat;
            tracker[cat].y = 0;
        });

        entData.forEach(datum => {
            const deviceKey = `${datum.device_name}_${datum.wwn}`;
            if (!uniqueDevices.hasOwnProperty(deviceKey) && !Boolean(datum.virtualized)) {
                uniqueDevices[deviceKey] = true;
                const latency = datum.latency;
                if (latency < .01) {
                    tracker[deviceCountsByLatencyCategories[0]].y = tracker[deviceCountsByLatencyCategories[0]].y + 1;
                } else if (latency >= .01 && latency < 1) {
                    tracker[deviceCountsByLatencyCategories[1]].y = tracker[deviceCountsByLatencyCategories[1]].y + 1;
                } else if (latency >= 1 && latency < 2) {
                    tracker[deviceCountsByLatencyCategories[2]].y = tracker[deviceCountsByLatencyCategories[2]].y + 1;
                } else {
                    tracker[deviceCountsByLatencyCategories[3]].y = tracker[deviceCountsByLatencyCategories[3]].y + 1;
                }
            }
        });

        return deviceCountsByLatencyCategories.map(cat => tracker?.[cat] ?? {});
    }

    const getEnterpriseSummary = async function () {
        const token = await getAccessTokenSilently();
        const route = `/api/v6/report/enterprise_summary_proc?client_id=${client.clientId}&period_id=${periodId}`;
        // const route = `/api/mock/enterprise_summary`;
        try {
            const data = await getReport(route, null, token);
            // const data = await axios.get(route);
            const narrowData = data?.data?.recordset;
            // const narrowData = data?.data[0].dataGrid;

            setEnterpriseGrid(narrowData || []);
            setDeviceCountByUtilPieData(formatDeviceCountByUtilPieData(narrowData || []));
            const deviceBarCounts = formatDeviceCountsBarData(narrowData);
            setDeviceCountBarData(
              formatBarData(deviceBarCounts, {
                sortingArr: deviceCountsByLatencyCategories,
                sortingField: 'name',
                defs: [{
                    headerName: '# Devices',
                    field: 'y',
                }],
            }).series);
            getDashboard(narrowData || []);
            setIsLoading(false);
        } catch (err) {
            console.error(err);
            setIsLoading(false)
        }
    };

    useEffect(() => {
        if (user) {
            getEnterpriseSummary();
        }
    }, [client, periodId, searchParams]);

    useEffect(() => {
        if (barChartDivRef.current && barChartDivRef?.current?.scrollHeight > redLightArrayHeight) {
            setRedLightArrayHeight(barChartDivRef.current.scrollHeight); // Update height based on the tracked component
        }
    }, [barChartDivRef.current, isLoading]); // Re-run effect if the referenced element changes
    const onRowClick = (e) => {
        navigate(`/reports/storage/device/device-summary?device_name=${encodeURI(e.data.device_name)}&wwn=${e.data.wwn}&period_id=${e.data.period_id}`)
    };

    const filterChanged = (eventData) => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if (node.data) {
                rowData.push(node.data);
            }
        });
        getDashboard(rowData || []);
        setDeviceCountByUtilPieData(formatDeviceCountByUtilPieData(rowData || []));
        setDeviceCountBarData(
          formatBarData(formatDeviceCountsBarData(rowData || []), {
            sortingArr: deviceCountsByLatencyCategories,
            sortingField: 'name',
              defs: [{
                headerName: '# Devices',
                  field: 'y',
              }],
        }).series);
    }

    const arraysWithRedLights = enterpriseGrid.filter(datum => parseFloat(datum.max_alert_level) === 0);

    const deleteDeviceBtn = <>
        <Button
            onClick={() => {
                setShowWarningModal(true);
            }}
            id={"upload-trash-btn"}
            disabled={!rowSelected}>
            <i className="fa fa-trash" aria-hidden="true"></i>
        </Button>
        <Modal className="warning-modal" backdrop="static" isOpen={showWarningModal}>
            <ModalHeader toggle={() => setShowWarningModal(false)}>Are you sure you want to delete
                Device {rowSelected?.device_name}</ModalHeader>
            <ModalFooter>
                <Button onClick={async () => {
                    const formData = new FormData();
                    formData.append("client_id", rowSelected.client_id);
                    formData.append("period_id", rowSelected.period_id);
                    formData.append("device_name", rowSelected.device_name);
                    formData.append("device_type_code", rowSelected.device_type_code);
                    const token = await getAccessTokenSilently();

                    PostAuth('/api/v6/upload/delete_device', formData, token)
                        .then(async data => {
                            await PostAuth('/api/v6/report/delete_enterprise_summary',
                                {id: rowSelected.enterprise_summary_id}, token);
                            getEnterpriseSummary();
                            setShowWarningModal(false);
                        });
                }
                } color="danger">Confirm Delete</Button>
                <Button color="secondary" onClick={() => {
                    setShowWarningModal(false)
                }}>Cancel</Button>
            </ModalFooter>
        </Modal>
    </>

    const onSelectionChanged = (gridRef) => {
        const selectedRow = gridRef?.current?.api?.getSelectedRows?.()?.[0];
        if (selectedRow) {
            setRowSelected(selectedRow);
        }
    };

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>
              Enterprise Summary
              {isLoading && <Spinner className={"ms-2"} size={"sm"} color={"secondary"}/>}
            </VsiH4>
            <Row className={`py-2`}>
                        <Col md={12} lg={4}>
                            <VsiBubble
                                autobotName='UsedAtAGlance'
                                autobotInfo={{ data: {
                                    atAGlance: {config: utilAag, data: atAGlanceData},
                                    progressBar: {config: percentUsedProgressConfig, data: dashboard}
                                }}}>
                                <div className="px-4">
                                    <Row>
                                        <Col xs={1}>
                                            <VsiSpanText className={"vsiBadge"}>
                                                <i className="fa-solid fa-database"/>
                                            </VsiSpanText>
                                        </Col>
                                        <Col xs={11}>
                                            <AtAGlance config={utilAag}
                                                       data={atAGlanceData}
                                                       isLoading={isLoading}
                                                       size={"lg"}/>
                                        </Col>
                                    </Row>
                                    <ProgressBar className={"mb-2"}
                                                data={percentUsedProgressConfig.fields.map((field) => dashboard?.[field] || 0)}
                                                denominator={percentUsedProgressConfig.denominator}
                                                title={percentUsedProgressConfig.title}
                                                colors={percentUsedProgressConfig.colors}
                                                isMulti={percentUsedProgressConfig.type === "multi"}
                                                thresholds={percentUsedProgressConfig.thresholds}
                                                multiply={percentUsedProgressConfig.multiply}
                                                size={"lg"}
                                            />
                                </div>
                            </VsiBubble>
                            <VsiBubble>
                                <DonutChart data={deviceCountByUtilPieData}
                                            alignTitle={'center'}
                                            title={"Device Count by Utilization %"}
                                            colors={entSummaryDeviceCountsColors}
                                            seriesName={'Device Count'}/>
                            </VsiBubble>
                        </Col>

                        <Col md={12} lg={4}>
                            <VsiBubble
                                autobotName='SummaryAlerts'
                                autobotInfo={{ data: {
                                    progressBar: {config: enterpriseProgressConfig, data: dashboard},
                                    redLights: {data: arraysWithRedLights},
                                    atAGlance: {config: miscAag, data: atAGlanceData}
                                }}}>
                                <div className={"d-flex flex-column justify-content-center px-4"} >
                                    {enterpriseProgressConfig.map((blueprints, index) => {
                                        const data = blueprints.fields.map((field) => dashboard?.[field] || 0);
                                        return (
                                            <ProgressBar
                                                className={"mb-2"}
                                                data={data}
                                                denominator={blueprints.denominator}
                                                title={blueprints.title}
                                                colors={blueprints.colors}
                                                key={`progress-bar-${blueprints.title
                                                    .toLowerCase()
                                                    .split(" ")
                                                    .join("-")}-${index}`}
                                                isMulti={blueprints.type === "multi"}
                                                thresholds={blueprints.thresholds}
                                                multiply={blueprints.multiply}
                                                size={"lg"}
                                            />
                                        )
                                    })}
                                    <Row className={"mt-2"}>
                                        <Col lg={12}>
                                            <VsiLgText>
                                                <span className={`healthAlert me-1`}>
                                                    <i className="fa-solid fa-bell"/>
                                                </span> Device{arraysWithRedLights.length !== 1 ? 's' : ''} with red
                                                lights ({arraysWithRedLights.length}):
                                            </VsiLgText>
                                            <VsiHr/>
                                            <Row className={"mb-4"}
                                                style={{maxHeight: 185 || null, overflowY: 'scroll'}}>
                                                {arraysWithRedLights.map((datum, i) => (
                                                    <Col lg={12} xl={6} key={`${datum.device_name}-device-alert-${i}`}>
                                                        <Link
                                                            to={`/reports/storage/device/device-summary?device_name=${encodeURI(datum.device_name)}&wwn=${datum.wwn}&period_id=${datum.period_id}`}
                                                            style={{textDecoration: "none", color: "inherit",}}>
                                                            <VsiH6 className={"d-flex align-items-center"}
                                                                    style={{cursor: 'pointer'}}>
                                                                - {datum.device_name.length > 18 ? datum.device_name.substring(0, 18) + '...' : datum.device_name}
                                                            </VsiH6>
                                                        </Link>
                                                    </Col>
                                                ))}
                                                {arraysWithRedLights.length === 0 ? (
                                                    <Col>
                                                        <VsiH6>
                                                            <i className="healthOk fa-solid fa-check me-1"/> None
                                                        </VsiH6>
                                                    </Col>
                                                ) : null}
                                            </Row>
                                        </Col>
                                        <Col lg={12}>
                                            <VsiLgText>
                                                <span className="me-1">
                                                    <i className="fa-solid fa-newspaper"/>
                                                </span> All details:
                                            </VsiLgText>
                                            <VsiHr/>
                                            <div
                                                style={{minHeight: (arraysWithRedLights.length > 8 ? 165 : 350) || null}}>
                                                {miscAag.map(config => {
                                                    const title = handleAagTitle(config.title, config.field);
                                                    const value = formatAagValue(atAGlanceData[config.field], config.field);
                                                    return (
                                                        <div key={`${config.field}-other-detail`}
                                                                className={"d-flex flex-row justify-content-between align-items-center mb-1"}>
                                                            <VsiH6 color={titleTextColor}>
                                                                {title}
                                                            </VsiH6>
                                                            <VsiH6>
                                                                {value}
                                                            </VsiH6>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </VsiBubble>
                        </Col>
                        <Col md={12} lg={4}>
                            <div ref={barChartDivRef}>
                                <VsiBubble
                                    autobotName='IopsAtAGlance'
                                    autobotInfo={{ data: {
                                        atAGlance: {config: countsAag, data: atAGlanceData},
                                        progressBar: {config: percentIopsProgressConfig, data: dashboard}
                                    }}}>
                                    <div className="px-4">
                                        <Row>
                                            <Col xs={1}>
                                                <VsiSpanText className={"vsiBadge"}>
                                                    <i className="fa-solid fa-network-wired"/>
                                                </VsiSpanText>
                                            </Col>
                                            <Col xs={11}>
                                                <AtAGlance config={countsAag}
                                                           data={atAGlanceData}
                                                           isLoading={isLoading}
                                                           size={"lg"}/>
                                            </Col>
                                        </Row>
                                        <ProgressBar className={"mb-2"}
                                                        data={percentIopsProgressConfig.fields.map((field) => dashboard?.[field] || 0)}
                                                        denominator={percentIopsProgressConfig.denominator}
                                                        title={percentIopsProgressConfig.title}
                                                        colors={percentIopsProgressConfig.colors}
                                                        isMulti={percentIopsProgressConfig.type === "multi"}
                                                        thresholds={percentIopsProgressConfig.thresholds}
                                                        multiply={percentIopsProgressConfig.multiply}
                                                        size={"lg"}
                                                    />
                                    </div>
                                </VsiBubble>
                                <VsiBubble className="px-4">
                                    <ColumnChart categories={deviceCountsByLatencyCategories}
                                                    title={'Device Count by Latency'}
                                                    titleStyle={{
                                                        fontSize: '1rem',
                                                    }}
                                                    alignTitle={'center'}
                                                    yAxisTitle={"# Devices"}
                                                    disableLegend
                                                    height={400}
                                                    colors={beforeAfterOld}
                                                    series={deviceCountBarData}/>
                                </VsiBubble>
                            </div>
                        </Col>
                    </Row>
                    <VsiBubble>
                        <Grid addStyle data={enterpriseGrid}
                              gridText={'Double click to see detailed device reports'}
                              columnDefs={enterpriseGridDefs}
                              filterChanged={filterChanged}
                              rowSelection={'single'}
                              onSelectionChanged={onSelectionChanged}
                              customToolbarButton={user.role === "super_user" ? deleteDeviceBtn : <></>}
                              rowDoubleClicked={onRowClick}/>
                    </VsiBubble>
        </Card>
    )
}
